import type { StoreRegion } from "@medusajs/types";
import { createFileRoute } from "@tanstack/react-router";
import {
  FacetFilterSheet,
  FacetFilterSidebar,
} from "#/components/FacetFilters";
import { ProductSearchField } from "#/components/ProductSearchField";
import { useExclusiveSlugs } from "#/lib/exclusive";
import { cachedEnsureQueryData } from "#/lib/server-cache";
import {
  activeFilterCount,
  categoryHandlesFromSearch,
  clearAllPatch,
  type FacetMap,
  type FacetRootHandle,
  patchForRoot,
  rootSelections,
  type SetShopSearch,
  type ShopSearch,
  shopSearchSchema,
} from "#/lib/shop-search";
import { isSortKey, orderForSort } from "#/lib/sort";
import {
  type ProductFilters,
  productQueries,
  useProducts,
} from "#/queries/products";
import { regionKeys, regionQueries } from "#/queries/regions";
import { taxonomyQueries, useCategories } from "#/queries/taxonomy";
import { useUnlockStore } from "#/store/unlock";
import { Pagination } from "../components/Pagination.js";
import { ProductCard, ProductGridSkeleton } from "../components/ProductCard.js";

const PER_PAGE = 24;

export function filtersFromSearch(
  search: ShopSearch,
  limit = PER_PAGE,
): ProductFilters {
  return {
    q: search.q,
    categorySlugs: categoryHandlesFromSearch(search),
    includeFacets: true,
    order: orderForSort(search.sort),
    limit,
    offset: (search.page - 1) * limit,
  };
}

const REGION_TTL_MS = 10 * 60 * 1000;
const TAXONOMY_TTL_MS = 5 * 60 * 1000;
const PRODUCT_TTL_MS = 60 * 1000;

export const Route = createFileRoute("/shop")({
  validateSearch: shopSearchSchema,
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ deps, context: { queryClient } }) => {
    const filters = filtersFromSearch(deps.search);
    if (typeof window !== "undefined") {
      const region = queryClient.getQueryData<StoreRegion | null>(
        regionKeys.default,
      );
      if (region !== undefined) {
        void queryClient.prefetchQuery(
          productQueries.list(region?.id, filters),
        );
      }
      return;
    }
    const region = await cachedEnsureQueryData<StoreRegion | null>(
      queryClient,
      regionQueries.default(),
      REGION_TTL_MS,
    );
    await Promise.all([
      cachedEnsureQueryData(
        queryClient,
        productQueries.list(region?.id, filters),
        PRODUCT_TTL_MS,
      ),
      cachedEnsureQueryData(
        queryClient,
        taxonomyQueries.categories(),
        TAXONOMY_TTL_MS,
      ),
    ]);
  },
  head: () => ({ meta: [{ title: "Shop" }] }),
  component: ShopRoute,
});

function ShopRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const setShopSearch: SetShopSearch = (next) => {
    navigate({
      search: (prev: ShopSearch) => ({ ...prev, ...next }),
      replace: true,
      resetScroll: false,
    });
  };
  return <ShopPage search={search} setShopSearch={setShopSearch} />;
}

interface ShopPageProps {
  search: ShopSearch;
  setShopSearch: SetShopSearch;
}

function ShopPage({ search, setShopSearch }: ShopPageProps) {
  const { data: categoriesData } = useCategories();
  const categories = categoriesData ?? [];
  const unlockedSlugs = useUnlockStore((s) => s.unlockedSlugs);
  const exclusiveSlugs = useExclusiveSlugs();

  const { data } = useProducts(filtersFromSearch(search), {
    keepPreviousData: true,
  });
  const isLoading = data === undefined;
  const facets: FacetMap | null = data?.facets ?? null;
  const facetSelections = rootSelections(search);

  const lockedExclusiveCategoryIds = new Set(
    categories
      .filter(
        (c) => exclusiveSlugs.has(c.slug) && !unlockedSlugs.includes(c.slug),
      )
      .map((c) => c.id),
  );
  const exclusiveCategoryIds = new Set(
    categories.filter((c) => exclusiveSlugs.has(c.slug)).map((c) => c.id),
  );

  const visibleItems = (data?.items ?? []).filter(
    (p) => !lockedExclusiveCategoryIds.has(p.metadata.categoryId),
  );
  const sorted = [...visibleItems];
  if (search.sort === "price-asc")
    sorted.sort((a, b) => a.metadata.priceAmount - b.metadata.priceAmount);
  if (search.sort === "price-desc")
    sorted.sort((a, b) => b.metadata.priceAmount - a.metadata.priceAmount);

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PER_PAGE));

  const filterCount = activeFilterCount(search);

  const onSelectFacet = (root: FacetRootHandle, next: string[]) =>
    setShopSearch(patchForRoot(root, next));

  const clearAllFilters = () => setShopSearch(clearAllPatch());

  return (
    <div data-screen-label="Shop" className="pt-12 md:pt-16 pb-24">
      <div className="mx-auto w-full max-w-figma px-6 md:px-10">
        <div className="mb-8 flex justify-end">
          <ProductSearchField
            q={search.q}
            onCommit={(q) => setShopSearch({ q, page: 1 })}
            scope={{ categorySlugs: categoryHandlesFromSearch(search) }}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10 lg:gap-14">
          <FacetFilterSidebar
            title="Categories"
            facets={facets}
            selections={facetSelections}
            onSelect={onSelectFacet}
            activeFilterCount={filterCount}
            onClearAll={clearAllFilters}
          />

          <div>
            <div className="mb-6 flex items-center justify-between gap-3">
              <div className="font-display text-base text-lp-text">
                {isLoading ? "Loading…" : `${data.total} results`}
              </div>
              <div className="flex items-center gap-3">
                <FacetFilterSheet
                  title="Categories"
                  facets={facets}
                  selections={facetSelections}
                  onSelect={onSelectFacet}
                  activeFilterCount={filterCount}
                  onClearAll={clearAllFilters}
                />
                <select
                  value={search.sort}
                  onChange={(e) => {
                    if (isSortKey(e.target.value))
                      setShopSearch({ sort: e.target.value, page: 1 });
                  }}
                  className="h-10 cursor-pointer rounded-full border-2 border-lp-green bg-white px-4 font-display text-lp-text text-sm outline-none"
                >
                  <option value="default">Default Sorting</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="name">Name (A–Z)</option>
                </select>
              </div>
            </div>

            {isLoading ? (
              <ProductGridSkeleton count={PER_PAGE} />
            ) : sorted.length === 0 ? (
              <div className="py-24 text-center font-display text-lp-text-lt">
                No products match your filters yet — try removing one.
              </div>
            ) : (
              <ul className="m-0 grid list-none grid-cols-2 md:grid-cols-3 gap-x-6 md:gap-x-12 gap-y-10 md:gap-y-14 p-0">
                {sorted.map((p) => (
                  <li key={p.id}>
                    <ProductCard
                      product={p}
                      brandName={p.metadata.publisher ?? "Learning Plus"}
                      exclusive={exclusiveCategoryIds.has(
                        p.metadata.categoryId,
                      )}
                    />
                  </li>
                ))}
              </ul>
            )}

            {!isLoading && (
              <Pagination
                page={search.page}
                total={totalPages}
                onPage={(page) => setShopSearch({ page })}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
